import { Status } from '../../../../types/redux/data/dataTypes';
import { specifyStatus } from '../../../../utilities/generalMappings';
import type { RiskOverviewMonitorFundRow } from '../../../../api/rs-hg/risk/riskOverview.types';

export type OverviewColumnFields =
  | 'index'
  | 'internalFundName'
  | 'fundName'
  | 'reports'
  | 'positionDate'
  | 'calculationDate'
  | 'currency'
  | 'netAssetValue'
  | 'commitmentValue'
  | 'valueAtRiskValue'
  | 'ucitsLaw'
  | 'prospectusStatus'
  | 'notes'
  | 'subFundRiskReports'
  | 'leverage'
  | 'riaifLaw'
  | 'cssfRiskSpreading'
  | 'act40Law'
  | 'nursLaw'
  | 'fivgLaw'
  | 'partiiLaw'
  | 'qiaifLaw'
  | 'supplementaryRules'
  | 'totalCommitmentPercent'
  | 'fundVar'
  | 'totalGrossExposurePercent'
  | 'market'
  | 'counterparty'
  | 'credit'
  | 'liquidity';

const AIFMD_SHARED_COLUMNS: OverviewColumnFields[] = [
  'index',
  'internalFundName',
  'fundName',
  'reports',
  'positionDate',
  'currency',
  'netAssetValue',
];

const AIFMD_MODERN_COLUMNS: OverviewColumnFields[] = [
  ...AIFMD_SHARED_COLUMNS,
  'totalCommitmentPercent',
  'fundVar',
  'totalGrossExposurePercent',
  'prospectusStatus',
  'notes',
];

const AIFMD_LEGACY_COLUMNS: OverviewColumnFields[] = [
  ...AIFMD_SHARED_COLUMNS,
  'market',
  'counterparty',
  'credit',
  'liquidity',
  'notes',
];

/** Quayside / WCapital / Zebedee — Market / Counterparty / Credit / Liquidity columns. */
const AIFMD_LEGACY_CLIENTS = new Set(['quayside', 'wcapital', 'zebedee']);

/**
 * Temporary function to get the columns for the overview table.
 * Eventually this will be moved to the backend.
 */
export function getOverviewColumnsFields(
  clientName: string | null,
  sectionID?: string,
): OverviewColumnFields[] | null {
  // ----------------- CLIENT SPECIFIC COLUMN CONFIGURATIONS -----------------

  if (
    clientName &&
    AIFMD_LEGACY_CLIENTS.has(clientName) &&
    sectionID === 'aifmd'
  ) {
    return AIFMD_LEGACY_COLUMNS;
  }

  if (clientName === 'pure') {
    if (sectionID === 'ucits') {
      return [
        'index',
        'internalFundName',
        'fundName',
        'reports',
        'subFundRiskReports',
        'positionDate',
        'calculationDate',
        'currency',
        'netAssetValue',
        'commitmentValue',
        'valueAtRiskValue',
        'ucitsLaw',
        'prospectusStatus',
        'notes',
      ];
    } else if (sectionID === 'fivg') {
      return [
        'index',
        'internalFundName',
        'fundName',
        'reports',
        'subFundRiskReports',
        'positionDate',
        'calculationDate',
        'currency',
        'netAssetValue',
        'commitmentValue',
        'valueAtRiskValue',
        'fivgLaw',
        'prospectusStatus',
        'notes',
      ];
    } else if (sectionID === 'partii') {
      return [
        'index',
        'internalFundName',
        'fundName',
        'reports',
        'subFundRiskReports',
        'positionDate',
        'calculationDate',
        'currency',
        'netAssetValue',
        'commitmentValue',
        'valueAtRiskValue',
        'partiiLaw',
        'prospectusStatus',
        'notes',
      ];
    } else if (sectionID === 'sif-raif') {
      return [
        'index',
        'internalFundName',
        'fundName',
        'reports',
        'subFundRiskReports',
        'positionDate',
        'currency',
        'netAssetValue',
        'leverage',
        'cssfRiskSpreading',
        'prospectusStatus',
        'notes',
      ];
    }
  }

  // ----------------- DEFAULT COLUMN CONFIGURATIONS -----------------

  if (sectionID === 'ucits') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'ucitsLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'qiaif') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'leverage',
      'valueAtRiskValue',
      'qiaifLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'riaif') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'leverage',
      'valueAtRiskValue',
      'riaifLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'nurs') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'nursLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'act40') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'act40Law',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'fivg') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'fivgLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'trust') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'sif-raif') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'currency',
      'netAssetValue',
      'leverage',
      'cssfRiskSpreading',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'partii') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'partiiLaw',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'fil') {
    return [
      'index',
      'internalFundName',
      'fundName',
      'reports',
      'positionDate',
      'calculationDate',
      'currency',
      'netAssetValue',
      'commitmentValue',
      'valueAtRiskValue',
      'prospectusStatus',
      'notes',
    ];
  } else if (sectionID === 'aifmd') {
    return AIFMD_MODERN_COLUMNS;
  }

  return null;
}

export function addSupplementaryRulesColumn(
  fields: OverviewColumnFields[] | null,
  overviewFunds: RiskOverviewMonitorFundRow[],
): OverviewColumnFields[] | null {
  if (!fields) return fields;
  const supplementaryRulesField = 'supplementaryRules' as OverviewColumnFields;

  const hasSupplementaryRulesData = overviewFunds.some(
    (fund) =>
      specifyStatus(String(fund.monitor_data?.Supplementary_Rules ?? '')) !==
      Status.NA,
  );

  if (!hasSupplementaryRulesData) {
    return fields.filter((field) => field !== supplementaryRulesField);
  }

  if (fields.includes(supplementaryRulesField)) return fields;
  const idx = fields.indexOf('prospectusStatus');
  if (idx === -1) return [...fields, supplementaryRulesField];
  return [
    ...fields.slice(0, idx + 1),
    supplementaryRulesField,
    ...fields.slice(idx + 1),
  ];
}
